import {AnalyticsProvider} from '@github-ui/analytics-provider'
import {ContextualGlobalSsoBanner} from '@github-ui/global-sso-banner-ui/contextual'
import {FilteredListPageLayout} from '@github-ui/layout-primitives/FilteredListPageLayout'
import {GLOBAL_SIDEBAR_STORAGE_KEY} from '@github-ui/layout-primitives/use-collapsible-sidebar-state'
import {useRouteQuery} from '@github-ui/react-core/future/use-route-query'
import {Outlet, useNavigation} from '@github-ui/react-router'
import {ReposFilter} from '@github-ui/repos-filter'
import {ShortcutEditingHeader} from '@github-ui/saved-views'

import {FinderHeader} from '../components/FinderHeader'
import {CollapsibleFinderSidebar} from '../components/FinderSidebar'
import {ReposFinderStateProvider, useReposFinderStateContext} from '../contexts/ReposFinderStateContext'
import {useReposFinderLayoutState} from '../hooks/use-repos-finder-layout-state'
import {reposFinderLayoutRoute} from './repos-finder-layout-route'

export function ReposFinderLayoutEntrypoint() {
  const {data: payload} = useRouteQuery(reposFinderLayoutRoute, 'mainQuery')
  return (
    <AnalyticsProvider appName="repos-finder-page" category="Finder page" metadata={{page: 'repos-finder'}}>
      <ContextualGlobalSsoBanner />
      <ReposFinderStateProvider payload={payload}>
        <ReposFinderLayout />
      </ReposFinderStateProvider>
    </AnalyticsProvider>
  )
}

function ReposFinderLayout() {
  const {shortcuts, selectedSidebarItem, editing, deleting, methods, reposFilterProps, initialExpanded} =
    useReposFinderLayoutState()
  const {fetchedQuery} = useReposFinderStateContext()
  const {state: navigationState} = useNavigation()

  const isLoading = navigationState === 'loading'

  return (
    <FilteredListPageLayout storageKey={GLOBAL_SIDEBAR_STORAGE_KEY} initialExpanded={initialExpanded}>
      <FilteredListPageLayout.Sidebar aria-label="Repository filters" dialogTitle="Repository filters" scrollable>
        <CollapsibleFinderSidebar
          selectedLink={selectedSidebarItem?.id || ''}
          onSelectLink={methods.selectSidebarLink}
          shortcuts={shortcuts}
          onCreateView={methods.createShortcut}
        />
      </FilteredListPageLayout.Sidebar>
      <FilteredListPageLayout.Header>
        {editing.shortcut ? (
          <>
            <FilteredListPageLayout.PageHeader className="pb-3">
              <FilteredListPageLayout.PageHeader.TitleArea className="min-width-0">
                <FilteredListPageLayout.PageHeader.Title as="h1" className="min-width-0">
                  Create view
                </FilteredListPageLayout.PageHeader.Title>
              </FilteredListPageLayout.PageHeader.TitleArea>
            </FilteredListPageLayout.PageHeader>
            <ShortcutEditingHeader
              renderFilter={filterProps => <ReposFilter {...filterProps} {...reposFilterProps} />}
              onSave={editing.saveAndDismiss}
              onDismiss={editing.dismiss}
              shortcut={editing.shortcut}
              setShortcutValue={editing.setValue}
              isSaving={editing.isSaving}
              errorMessage={editing.errorMessage}
            />
          </>
        ) : (
          <FinderHeader
            selectedSidebarItem={selectedSidebarItem}
            onDeleteView={deleting.deleteShortcut}
            onEditView={methods.editShortcut}
            onUpdateQueryView={methods.updateShortcut}
            isQueryModified={!isLoading && fetchedQuery !== selectedSidebarItem?.query}
            deleteErrorMessage={deleting.errorMessage}
          />
        )}
      </FilteredListPageLayout.Header>
      <FilteredListPageLayout.Content as="div">
        {!editing.shortcut && (
          <FilteredListPageLayout.FilterBar aria-label="Filter repositories">
            <ReposFilter {...reposFilterProps} />
          </FilteredListPageLayout.FilterBar>
        )}
        <FilteredListPageLayout.Results aria-label={selectedSidebarItem?.name || 'Search repositories'}>
          <Outlet />
        </FilteredListPageLayout.Results>
      </FilteredListPageLayout.Content>
    </FilteredListPageLayout>
  )
}
